import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { SelectProps } from './types';
import { styles } from './styles';
import { generateFieldId } from '@v2/lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, required, options, placeholder, className, id, ...props }, ref) => {
    const selectId = id || generateFieldId(label);

    return (
      <div className={styles.wrapper}>
        {label && (
          <label htmlFor={selectId} className={styles.label}>
            {label}
            {required && <span className={styles.requiredIndicator}>*</span>}
          </label>
        )}
        <div className={styles.fieldContainer}>
          <select
            ref={ref}
            id={selectId}
            className={clsx(
              styles.base,
              styles.focus,
              styles.padding,
              error ? styles.error : styles.defaultBorder,
              props.disabled && styles.disabled,
              className,
            )}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined}
            required={required}
            {...props}>
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div className={styles.iconContainer}>
            <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
          </div>
        </div>
        {error && (
          <p id={`${selectId}-error`} className={styles.errorMessage}>
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${selectId}-hint`} className={styles.hint}>
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export type { SelectProps, SelectOption } from './types';
export default Select;
